import React from 'react'
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Tooltip, Space, Input } from 'antd';
import { FaTrash, FaEdit } from 'react-icons/fa';
import { DeleteOutlined } from '@ant-design/icons';
import { CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import config from '../../config';
import ExcelQuestionImport from '../Commons/ExcelQuestionImport';
import ExportExcelButton from '../Commons/ExportExcelButton';
import PermissionGuard from "../Guards/PermissionGuard";
import { PERMISSIONS } from "../../constants/permissions";
import TagMultiFilter from "../Commons/TagMultiFilter";
import { buildQuestionFilter } from "../../utils/gridify";

const QuestionsGrid = () => {

    
    const navigate = useNavigate();

    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [searchFilters, setSearchFilters] = useState({
        title: "",
        QCode: "",
        tag: []
    });
    const [isExcelUploaderOpen, setIsExcelUploaderOpen] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
 const tagColors = ["bg-green-100 text-green-800", "bg-yellow-100 text-yellow-800", "bg-pink-100 text-pink-800"];

    const fetchQuestions = async (page = 1, pageSize = 10, filtersFromCaller = searchFilters) => {
        setLoading(true);
        try {
            const requestBody = {
                Page: page,
                PageSize: pageSize,
                Filter: buildQuestionFilter(filtersFromCaller),
                OrderBy: "",
            };

            const response = await axios.post(
                `${config.apiBaseUrl}/api/Questions/GetList`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            const data = response.data.data || {};
            const items = data.questions || [];
            const total = data.totalCount ?? data.count ?? 0;


            setQuestions(items);
            setPagination((prev) => ({ ...prev, total, current: page, pageSize }));
        } catch (err) {
            console.error("Fetch questions error:", err);
            toast.error(<p dir="rtl" className="font-custom">جستجو با خطا مواجه شد</p>);
            if (err.response?.status === 401) {
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (pagination) => {
        fetchQuestions(pagination.current, pagination.pageSize);
    };

    const fetchQuestionsWithFilters = (override = {}) => {
        const effectiveFilters = { ...searchFilters, ...override };
        fetchQuestions(1, pagination.pageSize, effectiveFilters);
    };

    const handleDelete = (id) => {
        toast.warn(
            <div className="text-right font-custom">
                <p>آیا از حذف این سوال مطمئن هستید؟</p>
                <div className="flex justify-end gap-2 mt-2">
                    <button
                        className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded"
                        onClick={() => confirmDelete(id)}
                    >
                        حذف
                    </button>
                    <button
                        className="bg-gray-500 hover:bg-gray-700 text-white px-3 py-1 rounded"
                        onClick={() => toast.dismiss()}
                    >
                        انصراف
                    </button>
                </div>
            </div>,
            { autoClose: false, icon: false }
        );
    };
    const confirmDelete = async (id) => {
        try {

            const response = await axios.delete(`${config.apiBaseUrl}/api/Questions/DeleteQuestion?id=${id}`, {
                headers: {
                    "Content-Type": "application/json",
                    "HeaderKey": config.HeaderKey
                },
                withCredentials: true,
            });

            if (!response.status || response.status !== 200) {
                toast.error(<p dir="rtl" className="font-custom">حذف با خطا مواجه شد</p>);
            }
            else {
                toast.success(<p dir="rtl" className="font-custom">حذف با موفقیت انجام شد</p>);
            }
            fetchQuestions(pagination.current, pagination.pageSize);
        } catch (err) {
            console.error("Delete error:", err);
            toast.error(<p dir="rtl" className="font-custom">حذف با خطا مواجه شد</p>);
        }
    };

    const handleBatchDelete = () => {
        toast.warn(
            <div className="text-right font-custom">
                <p>آیا از حذف {selectedRowKeys.length} سوال مطمئن هستید؟</p>
                <div className="flex justify-end gap-2 mt-2">
                    <button
                        className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded"
                        onClick={() => confirmBatchDelete()}
                    >
                        حذف
                    </button>
                    <button
                        className="bg-gray-500 hover:bg-gray-700 text-white px-3 py-1 rounded"
                        onClick={() => toast.dismiss()}
                    >
                        انصراف
                    </button>
                </div>
            </div>,
            { autoClose: false, icon: false }
        );
    };

    const confirmBatchDelete = async () => {
        try {
            const ids = selectedRowKeys.join('&id=');
            const response = await axios.delete(`${config.apiBaseUrl}/api/Questions/BatchDeleteQuestion?id=${ids}`, {
                headers: {
                    "Content-Type": "application/json",
                    "HeaderKey": config.HeaderKey
                },
                withCredentials: true,
            });

            if (!response.status || response.status !== 200) {
                toast.error(<p dir="rtl" className="font-custom">حذف با خطا مواجه شد</p>);
            } else {
                toast.success(<p dir="rtl" className="font-custom">{selectedRowKeys.length} سوال با موفقیت حذف شد</p>);
                setSelectedRowKeys([]);
            }
            fetchQuestions(pagination.current, pagination.pageSize);
        } catch (err) {
            console.error("Batch delete error:", err);
            toast.error(<p dir="rtl" className="font-custom">حذف با خطا مواجه شد</p>);
        }
    };

    const handleEdit = (record) => {
        navigate(`/dashboard/questions/form`, { state: { initialQuestion: record } });
    };

    const handleSearchChange = (e, field) => {
        const value = e?.target ? e.target.value : e;
        setSearchFilters((prev) => ({ ...prev, [field]: value }));
    };
    const ClearSuffix = ({ onClick, style }) => (
        <Tooltip title="پاک‌کردن">
            <CloseCircleOutlined
                onClick={onClick}
                style={{ cursor: "pointer", ...style, textAlign: 'left' }}
                role="button"
                aria-label="clear"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
            />
        </Tooltip>
    );
    
    useEffect(() => {
        fetchQuestions(pagination.current, pagination.pageSize);
    }, []);

    const columns = [
        {
            title: "عملیات",
            key: "actions",
            fixed: "left",
            width: 120,
            className: "font-custom ",
            align: "center",
            render: (row) => (
                <div className="flex gap-2 justify-center">
                    <PermissionGuard permission={PERMISSIONS.QUESTIONS_DELETE}>
                        <Button
                            type="link"
                            className="text-red-500"
                            onClick={() => handleDelete(row.id)}
                            icon={<FaTrash size={18} color="#980000" />}
                        />
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSIONS.QUESTIONS_EDIT}>
                        <Button
                            type="link"
                            className="text-blue-500"
                            onClick={() => handleEdit(row)}
                            icon={<FaEdit size={18} />}
                        />
                    </PermissionGuard>
                </div>
            ),
        },
        {
            title: "تاریخ ایجاد",
            dataIndex: "createDatetime",
            key: "createDatetime",
            className: "font-custom ",
            width: 150,
            align: "center",
            render: (text) => text ? new Date(text).toLocaleDateString('fa-IR') : 'N/A',
        },
        {
            title: "کد سوال",
            dataIndex: "qCode",
            key: "qCode",
            width: 150,
            className: "font-custom ",
            align: "center",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        type='number'
                        placeholder="جستجوی کد سوال"
                        value={searchFilters.QCode}
                        onChange={(e) => handleSearchChange(e, "QCode")}
                        onPressEnter={() => fetchQuestionsWithFilters()}
                        suffix={
                            <ClearSuffix
                                onClick={() => {
                                    fetchQuestionsWithFilters({ QCode: "" });
                                    setSearchFilters((prev) => ({ ...prev, QCode: "" }));
                                }}
                                style={{ visibility: searchFilters.QCode ? "visible" : "hidden" }}
                            />
                        }
                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => fetchQuestionsWithFilters()}
                            icon={<SearchOutlined size={16} />}
                            size="small"
                            className="font-custom"
                        >
                            جستجو
                        </Button>
                    </Space>
                </div>
            )
        },
        {
            title: "تگ ها",
            dataIndex: "tags",
            key: "tags",
            width: 180,
            align: "center",
            className: "font-custom text-center",
            render: (tags) => (
                <div className="flex flex-wrap gap-1 justify-center">
                    {tags?.map((tag, index) => (
                        <span
                            key={index}
                            className={`${tagColors[index % tagColors.length]} text-xs font-semibold px-2 py-1 rounded`}>
                            {tag}
                        </span>
                    ))}
                </div>
            ),
            filterDropdown: () => (
                <TagMultiFilter
                    value={searchFilters.tag}
                    placeholder="تگ‌های سوال را وارد کنید"
                    onChange={(value) =>
                        setSearchFilters((prev) => ({ ...prev, tag: value }))
                    }
                    onSearch={(value) => {
                        const tag = Array.isArray(value)
                            ? value
                            : searchFilters.tag;
                        fetchQuestionsWithFilters({ tag });
                    }}
                />
            )
        },
        {
            title: "عنوان",
            dataIndex: "title",
            key: "title",
            className: "font-custom ",
            width: 500,
            align: "right",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="جستجوی عنوان"
                        value={searchFilters.title}
                        onChange={(e) => handleSearchChange(e, "title")}
                        onPressEnter={() => fetchQuestionsWithFilters()}
                        suffix={
                            <ClearSuffix
                                onClick={() => {
                                    fetchQuestionsWithFilters({ title: "" });
                                    setSearchFilters((prev) => ({ ...prev, title: "" }));
                                }}
                                style={{ visibility: searchFilters.title ? "visible" : "hidden" }}
                            />
                        }
                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => fetchQuestionsWithFilters()}
                            icon={<SearchOutlined size={16} />}
                            size="small"
                            className="font-custom"
                        >
                            جستجو
                        </Button>
                    </Space>
                </div>
            ),
        },
    ];



    return (
        <div className="font-custom rtl text-right p-5">
            <h1 className="text-center text-xl mt-5 mb-5">
                لیست سوالات
            </h1>
            
            
            <div className="flex justify-between">
                <div className="flex gap-2">
                    <PermissionGuard
                        permissions={[PERMISSIONS.QUESTIONS_CREATE, PERMISSIONS.QUESTIONS_EDIT]}
                        requireAll
                    >
                        <button
                            className="bg-blue-700 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-full shadow-md transition duration-300 mb-5"
                            onClick={() => setIsExcelUploaderOpen(true)}
                        >
                            آپلود اکسل
                        </button>
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSIONS.QUESTIONS_VIEW}>
                        <ExportExcelButton
                            endpoint={`${config.apiBaseUrl}/api/Questions/ExportExcel`}
                            filter={buildQuestionFilter(searchFilters)}
                            fileName="questions.xlsx"
                        />
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSIONS.QUESTIONS_CREATE}>
                        <button
                            className="bg-green-700 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-full shadow-md transition duration-300 mb-5"
                            onClick={() => navigate("form")}
                        >
                            + جدید
                        </button>
                    </PermissionGuard>
                </div>
                {selectedRowKeys.length > 0 && (
                    <PermissionGuard permission={PERMISSIONS.QUESTIONS_DELETE}>
                        <button
                            className="bg-red-600 hover:bg-red-500 text-white font-semibold py-2 px-4 rounded-full shadow-md transition duration-300 mb-5 flex items-center gap-2"
                            onClick={handleBatchDelete}
                        >
                            <DeleteOutlined />
                            حذف ({selectedRowKeys.length})
                        </button>
                    </PermissionGuard>
                )}
            </div>
            <ExcelQuestionImport
                open={isExcelUploaderOpen}
                onClose={() => setIsExcelUploaderOpen(false)}
                onSuccess={() => fetchQuestions(1, pagination.pageSize)}
            />
            <div className="w-full shadow-lg">
                <Table
                    columns={columns}
                    dataSource={questions}
                    loading={loading}
                    rowKey={(record) => record.id}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: (keys) => setSelectedRowKeys(keys),
                    }}
                    scroll={{ x: 1000 }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        total: pagination.total,
                        onChange: (page, pageSize) => {
                            setPagination((prev) => ({
                                ...prev,
                                current: page,
                                pageSize: pageSize,
                            }));
                            fetchQuestions(page, pageSize);
                        },
                    }}
                    bordered
                />
            </div>

            <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                className={"font-custom text-center rtl"}
                pauseOnHover
                theme="dark"
                transition={Bounce}
            />
        </div>
    )
}

export default QuestionsGrid